import { useMemo } from 'react';
import endPoints from 'utils/endPoints';
import { PostMutate, PutMutate } from 'utils/mutationAndQuery/customMutation';
import { FetchAllData } from 'utils/mutationAndQuery/customUseQuery';

export const onCreateShop = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PostMutate(endPoints.createShop);

  return {
    loading: isPending,
    submitData: mutate,
    message: message,
    isSuccess,
    isError,
    clearData,
  };
};

export const onLoginUser = () => {
  const {
    data,
    message,
    isPending,
    mutate,
    isSuccess,
    isError,
    clearData,
    accessToken,
    refreshToken,
  } = PostMutate(endPoints.Login);
  return {
    loading: isPending,
    submitData: mutate,
    message: message,
    isSuccess,
    isError,
    clearData,
    data,
    accessToken,
    refreshToken,
  };
};

export const loginAdmin = onLoginUser;

export const useFetchUserData = () => {
  const CACHE_KEY = 'fuko_cached_user_data';

  // Get cached data for fallback only if an active session exists
  const getCachedData = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  const token = localStorage.getItem('accessToken');
  const { data, isLoading, isSuccess, refetch } = FetchAllData({
    url: endPoints.userData,
    enabled: !!token,
  });

  // Cache fresh data and seed context headers when available
  if (data?.data && isSuccess) {
    try {
      const d = data.data;
      localStorage.setItem(CACHE_KEY, JSON.stringify(d));

      if (d.shop_id || d.business_id) {
        const sId = String(d.shop_id || d.business_id);
        localStorage.setItem('shop_id', sId);
        localStorage.setItem('active_shop_id', sId);
      }
      if (d.business_type_id || d.shop?.business_type_id) {
        localStorage.setItem('business_type_id', String(d.business_type_id || d.shop?.business_type_id));
      }
      if (d.business_type) {
        localStorage.setItem('business_type', String(d.business_type));
        localStorage.setItem('shop_business_type', String(d.business_type));
      }
      if (d.branch_id || d.assigned_branch?.id) {
        const bId = String(d.branch_id || d.assigned_branch?.id);
        if (!localStorage.getItem('active_branch_id')) {
          localStorage.setItem('active_branch_id', bId);
        }
        if (!localStorage.getItem('fuko_selected_branch_id')) {
          localStorage.setItem('fuko_selected_branch_id', bId);
        }
      }
    } catch {
      // Ignore storage errors
    }
  }

  const cachedData = getCachedData();
  const rawUserData = data?.data ?? cachedData ?? null;
  const rawUserDataJson = JSON.stringify(rawUserData);

  const userData = useMemo(() => {
    if (!rawUserData) return null;
    return {
      ...rawUserData,
      branch_id: rawUserData.branch_id || rawUserData.assigned_branch?.id || null,
      assigned_branch_name:
        rawUserData.assigned_branch_name ||
        rawUserData.assigned_branch?.branch_name ||
        rawUserData.assigned_branch?.name ||
        '',
      business_type:
        rawUserData.business_type?.toLowerCase().includes('coffee') ||
        rawUserData.business_type?.toLowerCase() === 'cafe'
          ? 'cafe'
          : rawUserData.business_type,
    };
  }, [rawUserDataJson]);

  return {
    isLoading: !cachedData && isLoading, // Only show loading if no cache
    userData,
    message: data?.message,
    isSuccess,
    refetch,
  };
};

export const fetchAllUsers = () => {
  const { data, isLoading, isSuccess } = FetchAllData({
    url: endPoints.fetchUsers,
  });

  return {
    isLoadingUsers: isLoading,
    getUsers: data?.data ?? [],
    message: data?.message,
    isSuccess,
  };
};

export const userLogout = () => {
  const {
    data,
    isPending,
    isError,
    isSuccess,
    message,
    error,
    mutate,
    clearData,
  } = PostMutate(endPoints.logout);
  return {
    logoutUserData: data,
    loadLogoutUser: isPending,
    isLogoutUserError: isError,
    isLogoutUserSuccess: isSuccess,
    logoutUserMessage: message,
    logoutUserError: error,
    mutate,
    clearData,
  };
};

export const onRegisterUser = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PostMutate(endPoints.registerUser, endPoints.fetchUsers);

  return {
    loadRegister: isPending,
    requestRegisterUser: mutate,
    registerUserMessage: message,
    successResponse: isSuccess,
    errorResponse: isError,
    clearRegisterUser: clearData,
  };
};

export const fetchUserRoles = (businessType?: string) => {
  const { data, isLoading, isSuccess } = FetchAllData({
    url: endPoints.userRoles,
    params: businessType ? { business_type: businessType } : undefined,
    otherQueryKeys: businessType ? [businessType] : undefined,
  });

  return {
    isLoadingRoles: isLoading,
    userRoles: data?.data ?? [],
    message: data?.message,
    isSuccess,
  };
};

export const useFetchUserPermission = (roleId?: number, businessType?: string) => {
  const CACHE_KEY = `fuko_cached_permissions_${roleId || 'global'}_${businessType || 'all'}`;

  // Get cached data for fallback only if an active session exists
  const getCachedData = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  // Build params: always include business_type when available
  const params: Record<string, any> = {};
  if (roleId) params.role_id = roleId;
  if (businessType) params.business_type = businessType;

  const { data, isLoading, isSuccess } = FetchAllData({
    url: endPoints.managePermission,
    params: Object.keys(params).length > 0 ? params : undefined,
    otherQueryKeys: [roleId, businessType].filter(Boolean),
  });

  // Cache fresh data when available
  if (data?.data && isSuccess) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data.data));
    } catch {
      // Ignore storage errors
    }
  }

  const cachedData = getCachedData();
  
  // Normalize the permission data:
  // 1. Ensure it's an array
  // 2. Extract permission objects and compute a flat list of keys for convenience
  let rawPermissions = data?.data ?? cachedData ?? [];
  if (!Array.isArray(rawPermissions)) {
    rawPermissions = [];
  }

  // userPermission: Array of objects for legacy compatibility (role_id, is_allowed, etc.)
  const userPermission = rawPermissions.map((p: any) => {
    if (typeof p === 'number') return { feature_key: p, is_allowed: true };
    return p;
  });

  // permissionKeys: Array of numbers for new whitelist logic (Dashboard, Sidebar)
  const permissionKeys: number[] = rawPermissions.map((p: any) => {
    if (typeof p === 'number') return p;
    if (typeof p === 'object' && p !== null && 'feature_key' in p) {
      return p.is_allowed ? p.feature_key : null;
    }
    return null;
  }).filter((p: number | null): p is number => p !== null);

  return {
    isLoadingPermission: !cachedData && isLoading,
    userPermission,
    permissionKeys,
    message: data?.message,
    isSuccess,
  };
};

export const requestUpdatePermission = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PostMutate(endPoints.rolePermissions, endPoints.managePermission);

  return {
    loadUpdatePermission: isPending,
    updatePermission: mutate,
    updatePermissionMessage: message,
    successResponse: isSuccess,
    errorUpdatePermissionResponse: isError,
    clearUpdatePermission: clearData,
  };
};

export const requestUpdateUserProfile = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PutMutate(endPoints.updateUserProfile, endPoints.fetchUsers);

  return {
    loadUpdateUserProfile: isPending,
    updateUserProfile: mutate,
    updateUserProfileMessage: message,
    successResponse: isSuccess,
    errorUpdateUserProfileResponse: isError,
    clearUpdateUserProfile: clearData,
  };
};

export const requestResetUserPassword = () => {
  const {
    data,
    message,
    isPending,
    mutate,
    isSuccess,
    isError,
    clearData,
    error,
  } = PutMutate(endPoints.resetPassword);

  return {
    loadResetPassword: isPending,
    resetPassword: mutate,
    resetPasswordResponse: message,
    successResetPassword: isSuccess,
    errorResetPassword: isError,
    clearResetPasswordResponse: clearData,
    responseData: data?.data,
    responseError: error,
  };
};

export const requestChangeLanguage = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PutMutate(endPoints.changeLanguage);

  return {
    loadChangeLang: isPending,
    changeLang: mutate,
    changeLangResponse: message,
    successChangeLang: isSuccess,
    errorChangeLang: isError,
    clearChangeLangResponse: clearData,
  };
};

export const onAddNewRole = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PostMutate(endPoints.addNewUserRole, endPoints.userRoles);

  return {
    loadAddRole: isPending,
    requestAddRole: mutate,
    addRoleMessage: message,
    successAddRole: isSuccess,
    errorAddRole: isError,
    clearAddRole: clearData,
  };
};

export const onUpdatePrinterSettings = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PutMutate(endPoints.updatePrinterSettings, endPoints.userData);

  return {
    loadUpdateSettings: isPending,
    updateSettings: mutate,
    updateSettingsMessage: message,
    successUpdateSettings: isSuccess,
    errorUpdateSettings: isError,
    clearUpdateSettings: clearData,
  };
};
export const onUpdateShopProfile = () => {
  const { message, isPending, mutate, isSuccess, isError, clearData } =
    PutMutate(endPoints.updateShopProfile, endPoints.userData);

  return {
    loadUpdateShopProfile: isPending,
    updateShopProfile: mutate,
    updateShopProfileMessage: message,
    successUpdateShopProfile: isSuccess,
    errorUpdateShopProfile: isError,
    clearUpdateShopProfile: clearData,
  };
};
